// public/js/features/assets/settings-asset-fields.js
// Settings → Asset Fields tab.
//   • Superadmin: manage the option lists (and default label) of each optional
//     dropdown field (Asset type, Engine type, …).
//   • Tenant admin: per tenant, switch each field on/off and rename it.
import { getCurrentUser, can, isAdminLevel } from '../../modules/rbac.js';
import { loadTenants, updateTenant } from '../../modules/tenants.js';
import { scopeTenantParam } from '../../modules/tenant-scope.js';
import { scopePromptHtml } from '../../ui/tenant-scope-widget.js';
import { loadAssetFields, effectiveFieldLabel, fieldVisibleForTenant, saveAssetFieldOptions, patchAssetField } from './asset-fields.js';
import { FIELD_LABEL_KEYS, DEFAULT_ASSET_FIELD_LABELS, CUSTOM_TEXT_FIELDS, FIXED_TEXT_FIELDS } from './field-labels.js';
import { helpIcon, initHelp } from '../../ui/help.js';

// In-app help for the Asset Fields tab — see docs/help-text.md.
const HELP = {
  idFields:
    '<b>Identification fields</b><br>' +
    'The three text fields that name an asset — reference, label and serial. Renaming them here changes only ' +
    '<b>this tenant’s</b> forms and table headers; the stored data is untouched. Blank = the platform default.' +
    '<br><br><b>Aviation:</b> a charter operator relabels “Reference” to <i>Tail number</i>, while an MRO ' +
    'calls it <i>Work order</i>.',
  optFields:
    '<b>Optional dropdown fields</b><br>' +
    'Pre-defined pick-lists (Asset type, Engine type, …). The switch shows or <b>hides</b> a field everywhere ' +
    'for this tenant; the text box renames it. Off = the field never appears in this tenant’s forms, table or ' +
    'filters.' +
    '<br><br><b>Aviation:</b> a ground-handling tenant hides “Engine type” — irrelevant for baggage carts.',
  options:
    '<b>Options — the pick-list</b><br>' +
    'One choice per line. Write <code>value | Label</code>: the <b>value</b> is stored, the <b>Label</b> is ' +
    'shown (drop “| Label” to use the value as-is). Changing a value orphans assets already saved with the old ' +
    'one, so add a new option rather than rename.' +
    '<br><br><b>Aviation:</b> <code>turboprop | Turboprop</code>, <code>turbofan | Turbofan</code>, ' +
    '<code>piston | Piston</code>.',
  order:
    '<b>Order</b><br>' +
    'Sorts this field among the others (lower number = earlier). Put the fields your crews read most first.',
};

let selectedTenantId = null;

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

export async function renderAssetFieldSettings(container) {
  const groups = getCurrentUser().groups || [];
  const isSuper = isAdminLevel(groups);
  const canTenant = can(groups, 'manageTenants');
  if (!isSuper && !canTenant) {
    container.innerHTML = `<div class="alert alert-danger">Access denied.</div>`;
    return;
  }

  container.innerHTML = `<div class="text-muted py-3"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>`;

  let tenants, defs;
  try {
    [tenants, defs] = await Promise.all([loadTenants(), loadAssetFields(true)]);
  } catch (e) {
    container.innerHTML = `<div class="alert alert-danger">Failed to load asset fields.</div>`;
    return;
  }

  // Global tenant scope (hard-coupled): the sidebar selector is the ONLY
  // tenant choice. Under "All tenants" the per-tenant card becomes a prompt;
  // the platform field registry below stays scope-free by design.
  const scopeTid = scopeTenantParam();
  const scopePinned = scopeTid && scopeTid !== 'unassigned' && tenants.some(t => t.tenantId === scopeTid);
  const scopePrompted = tenants.length > 1 && !scopePinned;
  selectedTenantId = scopePinned ? scopeTid : (tenants[0]?.tenantId || null);
  const tenant = scopePrompted ? null : (tenants.find(t => t.tenantId === selectedTenantId) || null);
  const reload = () => renderAssetFieldSettings(container);

  // ── Per-tenant card: identification field names + optional dropdown fields ──
  // Visually highlighted (primary accent) to signal "applies only to this tenant".
  const tenantCard = scopePrompted ? scopePromptHtml('this tenant&#39;s field labels and visibility') : tenant ? `
    <div class="card shadow-sm mb-4 border-primary">
      <div class="card-header bg-primary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-building"></i>
        <span class="fw-semibold">Settings for <span class="tenant-name">${esc(tenant.name || selectedTenantId)}</span></span>
        <span class="badge bg-light text-primary ms-auto">applies to this tenant only</span>
      </div>
      <div class="card-body">
        <h6 class="text-primary mb-1"><i class="fa-solid fa-font me-2"></i>Identification fields${helpIcon('idFields')}</h6>
        <p class="text-muted small mb-3">Rename the text fields as they appear in this tenant's forms and table. Blank = default.</p>
        <div class="row g-2 mb-4">
          ${FIELD_LABEL_KEYS.map(k => `
            <div class="col-md-4">
              <label class="form-label small mb-1 text-muted">Default: ${esc(DEFAULT_ASSET_FIELD_LABELS[k])}</label>
              <input class="form-control form-control-sm af-textlabel" data-key="${k}" value="${esc(tenant.assetFieldLabels?.[k] || '')}" placeholder="${esc(DEFAULT_ASSET_FIELD_LABELS[k])}">
            </div>`).join('')}
        </div>

        <h6 class="text-primary mb-1"><i class="fa-solid fa-list-check me-2"></i>Optional dropdown fields${helpIcon('optFields')}</h6>
        <p class="text-muted small mb-3">Switch a field on/off (off = hidden everywhere for this tenant) and optionally rename it.</p>
        <div class="list-group mb-3">
          ${defs.map(f => `
            <div class="list-group-item d-flex align-items-center gap-3 af-row" data-field="${f.fieldId}">
              <div class="form-check form-switch m-0">
                <input class="form-check-input af-visible" type="checkbox" role="switch" ${fieldVisibleForTenant(f, tenant) ? 'checked' : ''}>
              </div>
              <input class="form-control form-control-sm af-rename" style="max-width:280px;" value="${esc(tenant.assetFieldLabels?.[f.fieldId] || '')}" placeholder="${esc(f.label)}">
              <span class="text-muted small ms-auto">${(f.options || []).length} options</span>
            </div>`).join('')}
        </div>

        <h6 class="text-primary mb-1"><i class="fa-solid fa-pen-to-square me-2"></i>Custom text fields</h6>
        <p class="text-muted small mb-3">Two extra free-text fields per asset. Switch one on and give it a title; off = hidden everywhere for this tenant.</p>
        <div class="list-group mb-3">
          ${CUSTOM_TEXT_FIELDS.map(cf => `
            <div class="list-group-item d-flex align-items-center gap-3 af-custom-row" data-key="${cf.key}">
              <div class="form-check form-switch m-0">
                <input class="form-check-input af-custom-visible" type="checkbox" role="switch" ${tenant.assetFieldVisibility?.[cf.key] ? 'checked' : ''}>
              </div>
              <input class="form-control form-control-sm af-custom-title" style="max-width:280px;" value="${esc(tenant.assetFieldLabels?.[cf.key] || '')}" placeholder="${esc(cf.defaultLabel)}">
            </div>`).join('')}
        </div>

        <h6 class="text-primary mb-1"><i class="fa-solid fa-lock me-2"></i>Standard fields (fixed name)</h6>
        <p class="text-muted small mb-3">Optional fields with a fixed, shared name that cannot be renamed — so the value means the same thing for everyone when shared or exported. Switch on to use.</p>
        <div class="list-group mb-3">
          ${FIXED_TEXT_FIELDS.map(ff => `
            <div class="list-group-item d-flex align-items-center gap-3 af-fixed-row" data-key="${ff.key}">
              <div class="form-check form-switch m-0">
                <input class="form-check-input af-fixed-visible" type="checkbox" role="switch" ${tenant.assetFieldVisibility?.[ff.key] ? 'checked' : ''}>
              </div>
              <span class="fw-medium">${esc(ff.label)}</span>
              <code class="text-muted small ms-auto">${esc(ff.key)}</code>
            </div>`).join('')}
        </div>

        <button id="af-save-tenant" class="btn btn-primary"><i class="fa-solid fa-check me-1"></i>Save tenant settings</button>
      </div>
    </div>` : `<div class="alert alert-info">You are not a member of any tenant.</div>`;

  // ── Superadmin: manage options + default label per field ──
  const superCard = isSuper ? `
    <div class="card shadow-sm mb-2 border-secondary">
      <div class="card-header bg-secondary text-white d-flex align-items-center gap-2">
        <i class="fa-solid fa-globe"></i><span class="fw-semibold">Field definitions</span>
        <span class="badge bg-light text-secondary ms-auto">global · superadmin · all tenants</span>
      </div>
      <div class="card-body">
        <p class="text-muted small mb-3">Default label and option list per field. One option per line as <code>value | Label</code> (Label optional). Applies to every tenant.</p>
        ${defs.map(f => `
          <div class="border rounded p-3 mb-3 af-def" data-field="${f.fieldId}">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="fw-semibold">${esc(f.fieldId)}</span>
            </div>
            <div class="row g-2 mb-2">
              <div class="col-sm-8"><label class="form-label small mb-1">Default label</label>
                <input class="form-control form-control-sm af-deflabel" value="${esc(f.label)}"></div>
              <div class="col-sm-4"><label class="form-label small mb-1">Order${helpIcon('order')}</label>
                <input type="number" class="form-control form-control-sm af-order" value="${f.order ?? 100}"></div>
            </div>
            <label class="form-label small mb-1">Options${helpIcon('options')}</label>
            <textarea class="form-control form-control-sm af-options" rows="6">${(f.options || []).map(o => `${o.value}${o.label && o.label !== o.value ? ' | ' + o.label : ''}`).join('\n')}</textarea>
            <button class="btn btn-sm btn-outline-primary mt-2 af-save-def"><i class="fa-solid fa-check me-1"></i>Save ${esc(f.fieldId)}</button>
          </div>`).join('')}
      </div>
    </div>` : '';

  container.innerHTML = tenantCard + superCard;
  initHelp(container, HELP);

  container.querySelector('#af-tenant')?.addEventListener('change', (e) => {
    selectedTenantId = e.target.value;
    reload();
  });

  // Save per-tenant visibility + renames (merge labels so text-field renames stay).
  container.querySelector('#af-save-tenant')?.addEventListener('click', async () => {
    const btn = container.querySelector('#af-save-tenant');
    btn.disabled = true;
    const labels = { ...(tenant.assetFieldLabels || {}) };
    const visibility = {};
    // Identification text fields (label/reference/serial) renames.
    container.querySelectorAll('.af-textlabel').forEach(i => {
      const k = i.dataset.key, v = i.value.trim();
      if (v) labels[k] = v; else delete labels[k];
    });
    // Optional dropdown fields: visibility + rename.
    container.querySelectorAll('.af-row').forEach(row => {
      const fid = row.dataset.field;
      visibility[fid] = row.querySelector('.af-visible').checked;
      const rename = row.querySelector('.af-rename').value.trim();
      if (rename) labels[fid] = rename; else delete labels[fid];
    });
    // Custom text fields: on/off + free title.
    container.querySelectorAll('.af-custom-row').forEach(row => {
      const k = row.dataset.key;
      visibility[k] = row.querySelector('.af-custom-visible').checked;
      const title = row.querySelector('.af-custom-title').value.trim();
      if (title) labels[k] = title; else delete labels[k];
    });
    // Fixed standard fields: on/off only (label is canonical, never renamed).
    container.querySelectorAll('.af-fixed-row').forEach(row => {
      visibility[row.dataset.key] = row.querySelector('.af-fixed-visible').checked;
    });
    try {
      await updateTenant(selectedTenantId, { assetFieldVisibility: visibility, assetFieldLabels: labels });
      window.showToast('Tenant field settings saved', 'success');
      reload();
    } catch (e) {
      window.showToast('Failed to save', 'danger');
      btn.disabled = false;
    }
  });

  // Superadmin: save a field's default label/order + options.
  container.querySelectorAll('.af-save-def').forEach(btn => {
    btn.addEventListener('click', async () => {
      const box = btn.closest('.af-def');
      const fieldId = box.dataset.field;
      const label = box.querySelector('.af-deflabel').value.trim();
      const order = Number(box.querySelector('.af-order').value) || 100;
      const options = box.querySelector('.af-options').value.split('\n')
        .map(line => line.trim()).filter(Boolean)
        .map(line => {
          const [value, ...rest] = line.split('|');
          const v = value.trim();
          const l = rest.join('|').trim();
          return { value: v, label: l || v };
        }).filter(o => o.value);
      btn.disabled = true;
      try {
        await patchAssetField(fieldId, { label, order, options });
        window.showToast(`Saved ${fieldId}`, 'success');
        reload();
      } catch (e) {
        window.showToast('Failed to save field', 'danger');
        btn.disabled = false;
      }
    });
  });
}
